import React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { Button } from '../../../common-components/Button/Button';
import { Field, FieldType } from '../../../common-components/Field/Field';
import { If } from '../../../common-components/If/If';
import { CollectTrackingResultType } from '../../../enums/CollectTrackingResultType';
import { PeriodTypeForOrdersInTransport } from '../../../enums/PeriodTypeForOrdersInTransport';
import { ProfileType } from '../../../enums/ProfileType';
import { ManageTransportHelper } from '../../../helpers/ManageTransportHelper';
import { strings } from '../../../resources/strings';
import { omnichannelResources } from '../../../resources/omnichannelResources';
import { Carrier } from '../../../models/Carrier';
import { ShopLocation } from '../../../models/ShopLocation';
import ServicesContext from '../../../contexts/ServicesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { MainActions } from '../../../actions/MainActions';
import { ContainerBody, Row } from './styles';

export interface IFilterToListOrdersInTransportOutput {
  numbers?: string;
  stateIds?: string;
  carrierCode?: string;
  shippingStartDate?: Date | null;
  shippingEndDate?: Date | null;
  virtualCollect?: string | null;
  lastConsultStatus?: string | null;
  periodType?: PeriodTypeForOrdersInTransport | null;
  stateFilterCode?: string;
  fulfilledBy?: string | null;
  originedBy?: string | null;
  locationId?: number | null;
  locationName?: string | null;
}

interface IModalFiltersToListOrdersInTransportProps {
  appliedFilters?: IFilterToListOrdersInTransportOutput;
  close: () => void;
  finish: (values: IFilterToListOrdersInTransportOutput) => void;
}

const ModalFiltersToListOrdersInTransport: React.FC<IModalFiltersToListOrdersInTransportProps> = ({
  appliedFilters,
  close,
  finish
}) => {

  const orderStateFilterValues = ManageTransportHelper.getOrderStateFilterValues();

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session, mainLocation } = useAuth();
  const [orderNumbers, setOrderNumbers] = React.useState<string | undefined>(appliedFilters?.numbers);
  const [stateFilterCode, setStateFilterCode] = React.useState<string | undefined>(appliedFilters?.stateFilterCode);
  const [carrierCode, setCarrierCode] = React.useState<string | undefined>(appliedFilters?.carrierCode);
  const [shippingStartDate, setShippingStartDate] = React.useState<Date | null>(appliedFilters?.shippingStartDate || null);
  const [shippingEndDate, setShippingEndDate] = React.useState<Date | null>(appliedFilters?.shippingEndDate || null);
  const [virtualCollect, setVirtualCollect] = React.useState<string | null>(appliedFilters?.virtualCollect || null);
  const [lastConsultStatus, setLastConsultStatus] = React.useState<string | null>(appliedFilters?.lastConsultStatus || null);
  const [periodType, setPeriodType] = React.useState<PeriodTypeForOrdersInTransport | null>(appliedFilters?.periodType || null);
  const [fulfilledBy, setFulfilledBy] = React.useState<string | undefined | null>(appliedFilters?.fulfilledBy);
  const [originedBy, setOriginedBy] = React.useState<string | undefined | null>(appliedFilters?.originedBy);
  const [carrierList, setCarrierList] = React.useState<Carrier[]>([]);
  const [loadingCarrierList, setLoadingCarrierList] = React.useState<boolean>(true);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [locationId, setLocationId] = React.useState<number | undefined | null>(appliedFilters?.locationId);
  const [locationName, setLocationName] = React.useState<string | undefined | null>(appliedFilters?.locationName);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  
  React.useEffect(() => {
    getCarrierList();
    getLocations();
  }, []);

  const {
    getCarrierListService,
    getLocationListService
  } = React.useContext(ServicesContext);

  const filterStateOrderRender = (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.StateOfOrder}
        selectProps={{
          options: [ ...orderStateFilterValues ],
          displayField: 'display',
          keyField: 'code'
        }}
        value={stateFilterCode || orderStateFilterValues[0].code}
        change={(value) => setStateFilterCode(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const memoFilterCarriersRender = React.useMemo(() => {
    return loadingCarrierList ? null : (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.Carrier}
          selectProps={{
            options: [{ code: '', name: strings.All }, ...carrierList],
            displayField: 'name',
            keyField: 'code'
          }}
          value={carrierCode ? carrierCode : ''}
          change={(value) => setCarrierCode(value)}
          marginTop={'0px'}
        />
      </Row>
    );
  }, [carrierList, carrierCode, loadingCarrierList]);

  const filterStateCollectRender = (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.VirtualCollect}
        selectProps={{
          options: [
            { id: '', name: strings.All },
            { id: 'NotDone', name: strings.NotDone },
            { id: CollectTrackingResultType[CollectTrackingResultType.Success], name: strings.SuccessfullyDone },
            { id: CollectTrackingResultType[CollectTrackingResultType.PendingSystem], name: strings.PendingAutomaticRealization },
            { id: CollectTrackingResultType[CollectTrackingResultType.PendingUser], name: strings.PendingManualRealization },
            { id: CollectTrackingResultType[CollectTrackingResultType.Failed], name: strings.WithFailed },
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={virtualCollect ? virtualCollect : ''}
        change={(value) => setVirtualCollect(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const filterLastConsultStatusTypeRender = (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.LastStatus}
        selectProps={{
          options: [
            { id: '', name: strings.All },
            { id: 'y', name: 'É um problema' },
            { id: 'n', name: 'Não é um problema' }
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={lastConsultStatus ? lastConsultStatus : null}
        change={(value) => setLastConsultStatus(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const filterPeriodTypeRender = (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.Period}
        selectProps={{
          options: [
            {
              id: PeriodTypeForOrdersInTransport.LastPackageStatus,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.LastPackageStatus)
            },
            {
              id: PeriodTypeForOrdersInTransport.OrderDispatched,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.OrderDispatched)
            },
            {
              id: PeriodTypeForOrdersInTransport.OrderCreated,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.OrderCreated)
            },
            {
              id: PeriodTypeForOrdersInTransport.LastPackageStatusAndOrderCreated,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.LastPackageStatusAndOrderCreated)
            },
            {
              id: PeriodTypeForOrdersInTransport.LastPackageStatusAndOrderDispatched,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.LastPackageStatusAndOrderDispatched)
            },
            {
              id: PeriodTypeForOrdersInTransport.OrderDispatchedAndOrderCreated,
              name: ManageTransportHelper.getDescriptionOfPeriodTypeForOrdersInTransport(PeriodTypeForOrdersInTransport.OrderDispatchedAndOrderCreated)
            }
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={periodType ? periodType : PeriodTypeForOrdersInTransport.LastPackageStatus}
        change={(value) => setPeriodType(+value)}
        marginTop={'0px'}
      />
    </Row>
  );

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}
      className='ModalFiltersToListOrdersInTransport'
    >
      <Modal.Header>
        <Modal.Title>
          Filtrar Pedidos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <ContainerBody>
          <Row>
            <Field
              type={FieldType.Text}
              label={strings.Orders}
              value={orderNumbers}
              change={(value) => changeOrderNumbers(value)}
              replaceEnterCodeByValue={' '}
              autoFocus={true}
              marginTop={'0px'}
            />
          </Row>
          <If condition={session?.user?.profileType === ProfileType.Vendor}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.FulfilledBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStore.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStore.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.Ecommerce.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={fulfilledBy}
                change={(value) => setFulfilledBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>
          <If condition={session?.user?.profileType === ProfileType.Ecommerce}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.OriginatedBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.Ecommerce.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={originedBy}
                change={(value) => setOriginedBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>
          <If condition={userLocations.length > 1 && !loadingLocations}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.Location}
                selectProps={{
                  options: [{ id: 0, name: strings.All }, ...locations.filter(l => userLocations.some(ulid => ulid === l.id))],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={locationId}
                change={(value) => setLocation(locations.find(l => l.id === +value))}
                marginTop={'0px'}
              />
            </Row>
          </If>
          {memoFilterCarriersRender}
          {filterPeriodTypeRender}
          <Row>
            <Field
              type={FieldType.DatePicker}
              label={'De'}
              marginRight='5px'
              value={shippingStartDate}
              change={(value) => setShippingStartDate(value)}
              marginTop={'0px'}
            />
            <Field
              type={FieldType.DatePicker}
              label={'Até'}
              marginLeft='5px'
              value={shippingEndDate}
              change={(value) => setShippingEndDate(value)}
              marginTop={'0px'}
            />
          </Row>
          {filterStateOrderRender}
          {filterStateCollectRender}
          {filterLastConsultStatusTypeRender}
        </ContainerBody>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button text={strings.Cancel} click={() => close()} marginRight={'10px'} />
          <Button text={strings.Confirm} click={() => filter()} marginLeft={'10px'} />
        </div>
      </Modal.Footer>
    </Modal>
  );

  function getCarrierList() {
    setLoadingCarrierList(true);
    mainActions.setNotWait(true);
    getCarrierListService.handle()
      .then(result => setCarrierList(result?.value?.carriers || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingCarrierList(false);
      });
  }

  function getLocations() {
    const manyLocations = userLocations.length > 1;
    if (!manyLocations) return;
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

  function setLocation(location?: ShopLocation | null) {
    setLocationId(location?.id);
    setLocationName(location?.name);
  }

  function changeOrderNumbers(value: string) {
    setOrderNumbers(value);
  }

  function filter() {
    finish({
      numbers: orderNumbers,
      stateFilterCode: stateFilterCode,
      shippingStartDate: shippingStartDate,
      shippingEndDate: shippingEndDate,
      carrierCode: carrierCode,
      virtualCollect: virtualCollect,
      lastConsultStatus: lastConsultStatus,
      periodType: periodType,
      fulfilledBy: fulfilledBy,
      originedBy: originedBy,
      locationId: locationId,
      locationName: locationName,
    });
  }
}

export { ModalFiltersToListOrdersInTransport };